import {
	GetPaymentGateWayResponseData,
	GetPaymentGatewayConstraintsResponse,
	PaymentChannelTab,
} from '@/modules/bank/type'
import { createColumns, writeAccessorKeys } from './constants'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useMemo, useState } from 'react'
import { SortableTable } from '@/common/ui/table/sortable-table'
import { SortableRow } from './SortableRow'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { BankQueryKeys } from '@/modules/bank/keys'
import useMutationUpdatePaymentGateway from '@/modules/bank/api/hooks/useMutationUpdatePaymentGateway'
import LoadingOverlay from '@/common/components/LoadingOverlay'

interface PaymentGatewayTableProps {
	data: GetPaymentGateWayResponseData[]
	total: number
	isLoading?: boolean
	hideLinkColumn?: boolean
	constraints?: GetPaymentGatewayConstraintsResponse
	channel?: PaymentChannelTab
}

export default function PaymentGatewayTable({
	data,
	total,
	isLoading = false,
	hideLinkColumn = false,
	constraints,
	channel,
}: PaymentGatewayTableProps) {
	const { data: user } = useQueryProfile()
	const { mutateAsync: updatePaymentGateway, isPending: isUpdatingOrder } =
		useMutationUpdatePaymentGateway()
	const queryClient = useQueryClient()
	const [isOrderChanging, setIsOrderChanging] = useState(false)

	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_BANK_ACCOUNT,
	)

	// Create columns with constraints for fallback values
	const columns = useMemo(
		() => createColumns(constraints, channel),
		[constraints, channel],
	)

	const filteredColumns = useMemo(
		() =>
			columns
				.filter(
					(column) =>
						hasWritePermission ||
						('accessorKey' in column &&
							!writeAccessorKeys.includes(column.accessorKey)),
				)
				.filter(
					(column) =>
						!hideLinkColumn ||
						!('accessorKey' in column) ||
						column.accessorKey !== 'link',
				),
		[columns, hasWritePermission, hideLinkColumn],
	)

	const handleOrderChange = async (
		_newData: GetPaymentGateWayResponseData[],
		movedItem: GetPaymentGateWayResponseData,
		newIndex: number,
	) => {
		if (!hasWritePermission) return
		// บัญชีถอน tab is for withdrawal-account toggle only; reorder uses QR/TRANSFER/TRUE_MONEY
		if (channel === 'WITHDRAW_BANK') return

		setIsOrderChanging(true)
		try {
			await updatePaymentGateway({
				payload: {
					id: movedItem.id,
					order: newIndex + 1,
					...(channel ? { channel: channel as string } : {}),
				},
			})
			queryClient.invalidateQueries({
				queryKey: [BankQueryKeys.PaymentGateways],
			})
			toast.success('อัพเดทลำดับสำเร็จ')
		} catch {
			toast.error('ไม่สามารถอัพเดทลำดับได้')
		} finally {
			setIsOrderChanging(false)
		}
	}

	return (
		<>
			<LoadingOverlay
				open={isOrderChanging || isUpdatingOrder}
				message="กำลังอัพเดทลำดับ..."
			/>
			<SortableTable
				data={data}
				columns={filteredColumns}
				searchPlaceholder="ค้นหาธนาคารออโต้"
				onOrderChange={handleOrderChange}
				SortableRowComponent={SortableRow}
				totalItems={total}
				isLoading={isLoading}
				disableDrag={channel === 'WITHDRAW_BANK'}
			/>
		</>
	)
}
